import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import doFetch from '../../../api/doFetch';
import * as moment from 'moment';

const context = createContext();
export const defaultValue = {
  source: null,
  dealers: [],
  duration: 1
};

export default function ({ children }) {
  const history = useHistory();

  const [filter, setFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);

    const urlDuration = params.get('duration');
    const urlSource = params.get('source');
    const urlDealers = params.getAll('dealers');
    const urlReasons = params.getAll('reasons');

    return {
      duration: urlDuration ? Number(urlDuration) : defaultValue.duration,
      source: urlSource
        ? urlSource === 'ALL'
          ? null
          : { label: urlSource, value: urlSource }
        : defaultValue.source,
      dealers:
        urlDealers.length > 0
          ? urlDealers.map((id) => ({ hmsId: Number(id) }))
          : defaultValue.dealers,
      failReasons: (urlReasons || []).map((value) => ({ value }))
    };
  });
  const [vehiclesStat, setVehiclesStat] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [dealers, setDealers] = useState([]);

  const getFailReasons = (dealer) => {
    const failReasons = [
      {
        name: 'CAPTURE CODE',
        value: ((100 / dealer.totalCount) * (dealer.failedCaptureCodeCount || 0)).toFixed(1)
      },
      {
        name: 'DATE TIME',
        value: ((100 / dealer.totalCount) * (dealer.failedDatetimeCount || 0)).toFixed(1)
      },
      {
        name: 'GEOPOSITION',
        value: ((100 / dealer.totalCount) * (dealer.failedGeopositionCount || 0)).toFixed(1)
      },
      {
        name: 'AI AUTHENTICITY',
        value: ((100 / dealer.totalCount) * (dealer.failedOriginalPhotoCount || 0)).toFixed(1)
      },
      {
        name: 'VIN',
        value: ((100 / dealer.totalCount) * (dealer.failedVinCount || 0)).toFixed(1)
      },
      {
        name: 'AI VIN AWARE',
        value: ((100 / dealer.totalCount) * (dealer.failedVisualMatchCount || 0)).toFixed(1)
      }
    ].sort((a, b) => (Number(a.value) > Number(b.value) ? -1 : 1));

    return failReasons;
  };

  const reloadData = async () => {
    setIsLoading(true);
    let query = `duration=${filter.duration}`;
    if (filter?.dealers?.length)
      query += filter.dealers.map(({ hmsId }) => `&hmsIds=${hmsId}`).join('');
    if (filter?.source?.value) query += `&provider=${filter.source.value}`;
    if (filter?.failReasons?.length)
      query += filter.failReasons.map(({ value }) => `&reasons=${value}`).join('');
    const endDate = moment().subtract(filter.duration, 'days').format('YYYY-MM-DD');

    const current = await doFetch(`/lotlenz/statistic?${query}`);
    const prev = await doFetch(`/lotlenz/statistic?${query}&endDate=${endDate}`);
    setDealers(
      current
        .map((dealer) => {
          const prevDealer = prev.find((d) => d.hmsId === dealer.hmsId);
          dealer.prevPassRate = prevDealer?.successCount
            ? Math.round((100 / prevDealer.totalCount) * prevDealer.successCount)
            : 0;
          dealer.passRate = dealer?.successCount
            ? Math.round((100 / dealer.totalCount) * dealer.successCount)
            : 0;
          dealer.passClassName =
            dealer.passRate > dealer.prevPassRate
              ? 'up'
              : dealer.passRate < dealer.prevPassRate
                ? 'down'
                : '';
          dealer.failClassName =
            dealer.passRate > dealer.prevPassRate
              ? 'down'
              : dealer.passRate < dealer.prevPassRate
                ? 'up'
                : '';

          dealer.failReasons = getFailReasons(dealer);

          return dealer;
        })
        .sort((a, b) => (a.dealershipName > b.dealershipName ? 1 : -1))
    );

    const vehiclesStat = await doFetch(`/lotlenz/statistic/general`);
    if (vehiclesStat) {
      vehiclesStat.vehiclesWithImages =
        vehiclesStat.vehiclesLotfloOnly +
        vehiclesStat.vehiclesHomenetOnly +
        vehiclesStat.vehiclesBoth;
      vehiclesStat.imagesPersent =
        Math.round((10000 / vehiclesStat.vehiclesTotal) * vehiclesStat.vehiclesWithImages) / 100;
      vehiclesStat.verificationsPersent =
        Math.round((10000 / vehiclesStat.vehiclesTotal) * vehiclesStat.verifiedTotol) / 100;
      vehiclesStat.successPersent =
        Math.round((10000 / vehiclesStat.imagesPersent) * vehiclesStat.verificationsPersent) / 100;
      setVehiclesStat(vehiclesStat);
    }
    setIsLoading(false);
  };

  const summary = useMemo(() => {
    if (!dealers?.length) return {};

    const summary = dealers.reduce(
      (summary, dealer) => {
        if (dealer?.totalCount) summary.totalCount += Number(dealer.totalCount || 0);
        if (dealer?.failedCount) summary.failedCount += Number(dealer.failedCount || 0);
        if (dealer?.successCount) summary.successCount += Number(dealer.successCount || 0);
        if (dealer?.failedCaptureCodeCount)
          summary.failedCaptureCodeCount += Number(dealer.failedCaptureCodeCount || 0);
        if (dealer?.failedDatetimeCount)
          summary.failedDatetimeCount += Number(dealer.failedDatetimeCount || 0);
        if (dealer?.failedOriginalPhotoCount)
          summary.failedOriginalPhotoCount += Number(dealer.failedOriginalPhotoCount || 0);
        if (dealer?.failedGeopositionCount)
          summary.failedGeopositionCount += Number(dealer.failedGeopositionCount || 0);
        if (dealer?.failedVisualMatchCount)
          summary.failedVisualMatchCount += Number(dealer.failedVisualMatchCount || 0);
        if (dealer?.failedVinCount) summary.failedVinCount += Number(dealer.failedVinCount || 0);

        return summary;
      },
      {
        totalCount: 0,
        failedCount: 0,
        successCount: 0,
        failedCaptureCodeCount: 0,
        failedDatetimeCount: 0,
        failedOriginalPhotoCount: 0,
        failedGeopositionCount: 0,
        failedVinCount: 0,
        failedVisualMatchCount: 0
      }
    );

    summary.passRate = summary?.successCount
      ? (Math.round((1000 / summary.totalCount) * summary.successCount) / 10).toFixed(1)
      : 0;
    summary.failReasons = getFailReasons(summary);

    return summary;
  }, [dealers]);

  useEffect(() => {
    reloadData().then(() => null);
    // eslint-disable-next-line
  }, [filter]);

  useEffect(() => {
    const params = new URLSearchParams();

    params.set('duration', filter.duration);
    params.set('source', filter.source?.value || 'ALL');
    if (filter.dealers?.length) {
      filter.dealers.forEach((d) => params.append('dealers', d.hmsId));
    }
    if (filter?.failReasons?.length) {
      filter.failReasons.forEach((v) => params.append('reasons', v.value));
    }

    const searchString = params.toString();
    history.replace({
      search: searchString ? `?${searchString}` : ''
    });
    // eslint-disable-next-line
  }, [filter]);

  const contextValue = useMemo(
    () => ({ filter, setFilter, isLoading, dealers, summary, vehiclesStat }),
    [filter, isLoading, dealers, summary, vehiclesStat]
  );

  return (
    <>{contextValue && <context.Provider value={contextValue}>{children}</context.Provider>}</>
  );
}

export const useData = () => {
  const { dealers, isLoading, summary, vehiclesStat } = useContext(context);
  return { dealers, isLoading, summary, vehiclesStat };
};

export const useFilter = () => {
  return useContext(context).filter;
};

export const usePatchFilter = () => {
  const { setFilter } = useContext(context);

  return async (patch) => {
    setFilter((filter) => ({ ...filter, ...patch }));
  };
};

export const useResetFilter = () => {
  const { setFilter } = useContext(context);

  return async () => {
    setFilter(defaultValue);
  };
};

export const usePerformExport = () => {
  const { filter } = useContext(context);

  return async (type) => {
    try {
      let url = `/vehicles/download/${type === 'csv' ? 'csv' : ''}?duration=${filter?.duration || 1}`;
      if (filter.source?.value) url += `&source=${filter.source.value}`;
      if (filter.dealers?.length)
        filter.dealers.forEach(({ hmsId }) => {
          url += `&dealer=${hmsId}`;
        });
      const response = await doFetch(url, { responseType: 'blob' });
      const blob = await response.blob();
      const file = new Blob([blob], { type: `application/${type}` });
      const fileURL = URL.createObjectURL(file);
      var a = document.createElement('a');
      a.href = fileURL;
      a.download = `Verification activity (${filter.duration}).${type}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      alert(error);
    }
  };
};
